import { memo, type ReactNode, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { useTrackViewedAsset } from '@air/analytics';
import { PlayerContextProvider } from '@air/component-player';
import { Portal } from '@air/primitive-portal';
import { useShortIdContext } from '@air/provider-short-id';

import { PublicAssetModalMobileLayout } from '~/components/PublicBoardAsset/components/PublicAssetModalMobileLayout';
import { usePublicSSOAssetCallback } from '~/components/PublicSSOCallback/usePublicSSOAssetCallback';
import { AssetModal } from '~/components/v2/components/asset-modal/AssetModal';
import { ZustandStorageKeys } from '~/constants/zustandStorageKeys';
import { usePublicAssetModalNavigation } from '~/providers/PublicAssetModalNavigationProvider';
import { clearCentralizedClipAction, setCentralizedClipAction } from '~/store/centralizedClip/actions';
import { centralizedClipAssetIdSelector, centralizedClipIsDemoSelector } from '~/store/centralizedClip/selectors';
import { type CentralizedClip } from '~/store/centralizedClip/types';
import { useGetPublicBoardClip } from '~/swr-hooks/shortUrl/useGetPublicBoardClip';
import { useAirStore } from '~/utils/ReduxUtils';

export type PublicAssetModalShellProps = {
  desktop: ReactNode;
};

export const PublicAssetModalShell = memo(({ desktop }: PublicAssetModalShellProps) => {
  const store = useAirStore();
  const dispatch = useDispatch();
  const { shortId } = useShortIdContext();
  const { activeClipId: clipId, onCloseAssetModal } = usePublicAssetModalNavigation();
  const { data: sharedClip } = useGetPublicBoardClip({ clipId: clipId ?? undefined, shortId });
  const { trackViewedAsset } = useTrackViewedAsset();

  usePublicSSOAssetCallback();

  useEffect(() => {
    if (sharedClip) {
      let clipData: CentralizedClip;

      if ('boardContext' in sharedClip && typeof sharedClip.boardContext === 'string') {
        clipData = { ...sharedClip, boards: [], boardContext: sharedClip.boardContext };
      } else {
        clipData = { ...sharedClip, boards: [] };
      }
      dispatch(setCentralizedClipAction({ clip: clipData }));
    }
  }, [sharedClip, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(clearCentralizedClipAction());
    };
  }, [dispatch]);

  useEffect(() => {
    if (clipId) {
      const assetId = centralizedClipAssetIdSelector(store.getState());
      const isDemo = centralizedClipIsDemoSelector(store.getState());

      trackViewedAsset({ assetId, isDemo, clip_id: clipId });
    }
  }, [clipId, store, trackViewedAsset]);

  const mobile = useMemo(
    () => <PublicAssetModalMobileLayout onCloseAssetModal={onCloseAssetModal} />,
    [onCloseAssetModal],
  );

  return (
    <Portal>
      <PlayerContextProvider id={clipId || shortId} storageKey={ZustandStorageKeys.AssetModalPlayer}>
        {/* Public share-link modals read and write discussions through the shortId'd public API. */}
        <AssetModal desktop={desktop} mobile={mobile} storeId={clipId || shortId} />
      </PlayerContextProvider>
    </Portal>
  );
});

PublicAssetModalShell.displayName = 'PublicAssetModalShell';
